import { useEffect, useMemo, useRef, useState } from 'react';
import * as esbuild from 'esbuild-wasm';
import { ensureDefaultExport, clamp } from '../utils';
import { PREVIEW_HTML_TEMPLATE } from '../constants';
import { useEditorState, usePreviewContext } from '../contexts';
import { DEVICE_PRESETS } from '../contexts/PreviewContext';

type UsePreviewArgs = {
  esbuildReady: boolean;
};

export function usePreview({ esbuildReady }: UsePreviewArgs) {
  // Get editor state from context
  const { code } = useEditorState();

  // Get preview state from context
  const { hotReloadEnabled, previewVersion, previewDevice, previewOrientation, customPreviewWidth } = usePreviewContext();
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [bundleUrl, setBundleUrl] = useState<string | null>(null);
  const previewQueueRef = useRef<{ running: boolean; lastSource: string | null }>({ running: false, lastSource: null });

  const previewSize = useMemo(() => {
    if (previewDevice === 'custom') {
      const width = clamp(customPreviewWidth, 320, 1600);
      const height = Math.round(width * 0.62);
      return previewOrientation === 'landscape'
        ? { width: Math.max(width, height), height: Math.min(width, height) }
        : { width, height };
    }

    const preset = DEVICE_PRESETS[previewDevice];
    const { width, height } = preset;

    // Apply orientation - swap width/height for landscape
    if (previewOrientation === 'landscape') {
      return { width: Math.max(width, height), height: Math.min(width, height) };
    }
    return { width: Math.min(width, height), height: Math.max(width, height) };
  }, [customPreviewWidth, previewDevice, previewOrientation]);

  useEffect(() => {
    if (!hotReloadEnabled) {
      if (bundleUrl) {
        URL.revokeObjectURL(bundleUrl);
        setBundleUrl(null);
      }
      setPreviewHtml('');
      return;
    }
    if (!esbuildReady || !code) return;

    let cancelled = false;
    const previousBundle = bundleUrl;

    const buildPreview = async () => {
      if (previewQueueRef.current.running) {
        previewQueueRef.current.lastSource = code;
        return;
      }
      previewQueueRef.current.running = true;
      previewQueueRef.current.lastSource = code;
      try {
        const source = ensureDefaultExport(previewQueueRef.current.lastSource);
        previewQueueRef.current.lastSource = null;
        const result = await esbuild.transform(source, {
          loader: 'tsx',
          jsx: 'automatic',
          format: 'esm',
          target: 'es2018',
        });
        if (cancelled) return;
        if (previousBundle) URL.revokeObjectURL(previousBundle);
        const compiled = result.code
          .replaceAll('from "react/jsx-runtime"', 'from "https://esm.sh/react@18.3.1/jsx-runtime"')
          .replaceAll("from 'react/jsx-runtime'", "from 'https://esm.sh/react@18.3.1/jsx-runtime'")
          .replaceAll('from "react/jsx-dev-runtime"', 'from "https://esm.sh/react@18.3.1/jsx-dev-runtime"')
          .replaceAll("from 'react/jsx-dev-runtime'", "from 'https://esm.sh/react@18.3.1/jsx-dev-runtime'")
          .replaceAll('from "react-dom/client"', 'from "https://esm.sh/react-dom@18.3.1/client"')
          .replaceAll("from 'react-dom/client'", "from 'https://esm.sh/react-dom@18.3.1/client'")
          .replaceAll('from "react"', 'from "https://esm.sh/react@18.3.1"')
          .replaceAll("from 'react'", "from 'https://esm.sh/react@18.3.1'");
        const blob = new Blob([compiled], { type: 'application/javascript' });
        const url = URL.createObjectURL(blob);
        setBundleUrl(url);
        setPreviewHtml(PREVIEW_HTML_TEMPLATE.replace('APP_BUNDLE_URL', url));
        setPreviewError(null);
      } catch (error) {
        console.error('preview build error', error);
        if (error instanceof Error) setPreviewError(error.message);
        else setPreviewError('Preview derleme hatası');
      } finally {
        previewQueueRef.current.running = false;
        if (previewQueueRef.current.lastSource && !cancelled) {
          void buildPreview();
        }
      }
    };

    const debounceTimer = window.setTimeout(() => void buildPreview(), 600);

    return () => {
      cancelled = true;
      window.clearTimeout(debounceTimer);
      if (previousBundle) URL.revokeObjectURL(previousBundle);
    };
  }, [bundleUrl, code, customPreviewWidth, esbuildReady, hotReloadEnabled, previewDevice, previewVersion]);

  return { previewHtml, previewError, bundleUrl, previewSize };
}
